import { useQuery } from '@tanstack/react-query';
import React from 'react';

import { ObjectCard } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { useNavigate } from '~/lib/navigation';
import { BankAccountRepository } from '~/repositories/BankAccountRepository';
import { useSessionStore } from '~/stores/Session';
import { BankAccountWithDetails } from '~/typings/API';

interface BankAccountCardProps {
  bankAccountId: string;
  availableBalance?: string;
}

const getBankAccountName = (bankAccount?: BankAccountWithDetails) => {
  if (!bankAccount) return undefined;
  return bankAccount.displayName || bankAccount.description || 'Unnamed Bank Account';
};

export const BankAccountCard: React.FC<BankAccountCardProps> = ({ bankAccountId, availableBalance }) => {
  const navigate = useNavigate();
  const { currentPlatform } = useSessionStore();

  const {
    data: bankAccount,
    isLoading,
    error,
  } = useQuery<BankAccountWithDetails>({
    queryKey: [currentPlatform?.id, 'bank-account', bankAccountId],
    queryFn: async () =>
      BankAccountRepository.get({
        id: bankAccountId,
      }),
    enabled: !!bankAccountId,
    gcTime: 30000,
    staleTime: 10000,
  });

  const bankAccountName = getBankAccountName(bankAccount);

  const handleNavigate = () => {
    navigate(`${ROUTE.BANK_ACCOUNTS}/edit/${bankAccountId}`);
  };

  return (
    <ObjectCard
      error={error ? 'Failed to load bank account' : undefined}
      icon="Bank"
      isLoading={isLoading}
      primaryText={bankAccountName || 'Unnamed Bank Account'}
      id={{
        label: 'ID:',
        value: bankAccountId,
        suffix: availableBalance ? `· Available: ${availableBalance}` : undefined,
      }}
      onClick={handleNavigate}
    />
  );
};
